import { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  Chip,
  ChipProps,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { State, WorkflowInvoke } from '@sierralabs/workflow';

import { PublishEventParams } from '../../../tasks/Task.machine';
import { useFindEventNames } from '../../Workflow.hooks';
import { WorkflowEntity } from '../../Workflow.interface';
import {
  ServiceConfiguratorProps,
  ServiceDisplayProps,
  ServicePlugin
} from '../ServicePlugin';

const StyledConfiguratorDiv = styled.div`
  .instructions {
    margin: 24px 0 16px 0;
  }
  .chip-error {
    color: ${props => props.theme.palette.error.main};
    font-weight: 500;
  }
`;

const PublishEventConfigurator: React.FC<ServiceConfiguratorProps> = props => {
  const { targetEntity, onChange } = props;
  const [invoke, setInvoke] = useState(props.invoke);
  if (!invoke.data) invoke.data = {};
  const data: PublishEventParams['data'] = invoke.data;
  const { eventList, isLoading, loadingError, findEvents } = useFindEventNames(
    (targetEntity || '') as WorkflowEntity
  );

  const update = (data: PublishEventParams['data']) => {
    const updatedInvoke = { ...invoke, data };
    setInvoke(updatedInvoke);
    onChange?.(updatedInvoke);
  };

  useEffect(() => {
    if (targetEntity) {
      findEvents();
    }
  }, [targetEntity, findEvents]);

  return (
    <StyledConfiguratorDiv>
      <Typography gutterBottom variant='body2' className='instructions'>
        Configure a server side event to be published when entering into this
        workflow state. Each workflow entity will have different events that can
        be configured.
      </Typography>
      <Autocomplete
        multiple
        className='field'
        filterSelectedOptions
        loading={isLoading}
        options={eventList || []}
        value={data?.events || []}
        getOptionLabel={option => option.description}
        getOptionSelected={(option, value) => option.event === value.event}
        renderTags={(value, getTagProps) => {
          // manually render chips to handle removed and deprecated events
          return value.map((option, index) => {
            const chipProps = getTagProps({ index }) as ChipProps;
            const classes: string[] = [];
            chipProps.className && classes.push(chipProps.className);

            // mark chip as deprecated if the eventList exists
            // but the option is not available in eventList
            const eventItem = eventList?.find(
              event => event.event === option.event
            );
            const deprecated = eventList && !eventItem;
            deprecated && classes.push('chip-error');

            const tooltip = deprecated
              ? `The event "${option.event}" is not available ` +
                'for this Workflow entity or it may have been deprecated.'
              : '';

            return (
              <Tooltip arrow key={`${option.event}-${index}`} title={tooltip}>
                <Chip
                  {...chipProps}
                  label={option.description}
                  className={classes.join(' ')}
                />
              </Tooltip>
            );
          });
        }}
        onChange={(_, value) => update({ ...data, events: value })}
        renderInput={params => (
          <TextField
            {...params}
            size='small'
            variant='outlined'
            label='Events'
            placeholder='Select event'
            error={!!loadingError}
            helperText={loadingError?.message}
          />
        )}
      />
    </StyledConfiguratorDiv>
  );
};

const StyledDisplayDiv = styled.div`
  font-size: 13px;
  padding-left: 4px;
  text-indent: -4px;
  code {
    background: #f4f5fa;
    padding: 2px 5px;
    border-radius: 3px;
  }
  b {
    padding: 0 5px 0 0;
  }
`;

const PublishEventDisplay: React.FC<ServiceDisplayProps> = ({ invoke }) => {
  return (
    <StyledDisplayDiv>
      {invoke.data?.events?.map((event: any, index: number) => (
        <div className='line' key={index}>
          {event.description || event.event}
        </div>
      ))}
    </StyledDisplayDiv>
  );
};

export const PublishEventPlugin: ServicePlugin<any> = {
  src: 'publishEvent',
  name: 'Publish Event',
  configurator: PublishEventConfigurator,
  display: PublishEventDisplay,
  validate: (condition: Partial<WorkflowInvoke>) => {
    return true;
  },
  service: (state: State<any>, params: PublishEventParams) => {
    console.log('publishEvent state', state);
    console.log('publishEvent params', params);
    return true;
  }
};
